// src/SallerAdmin/pages/Profile/BankDetailsPage.tsx
import React, { useState, useEffect } from 'react';
import { useUser } from '../../../contexts/UserContext';
import { Building, User, Landmark, Save, Loader2 } from 'lucide-react';

const ReadOnlyField = ({ label, value }: any) => (
    <div className="mb-4">
        <label className="block text-xs font-bold text-gray-500 uppercase mb-1">{label}</label>
        <div className="p-2 bg-gray-100 border border-gray-200 rounded text-gray-800 font-medium h-10 flex items-center">
            {value || 'N/A'}
        </div>
    </div>
);

const FormInput = ({ label, value, onChange, name, placeholder, required = false }: any) => (
    <div className="mb-4">
        <label className="block text-sm font-bold text-gray-700 mb-1">
            {label} {required && <span className="text-red-500">*</span>}
        </label>
        <input
            type="text"
            name={name}
            value={value}
            onChange={onChange}
            placeholder={placeholder}
            className="w-full border border-gray-300 rounded-lg p-2 focus:ring-2 focus:ring-blue-900 outline-none"
        />
    </div>
);

const BankDetailsPage: React.FC = () => {
    const { user } = useUser();
    const [loading, setLoading] = useState(false);
    
    // Data States
    const [businessData, setBusinessData] = useState<any>({});
    const [sellerData, setSellerData] = useState<any>({});
    const [bankData, setBankData] = useState({
        accountHolderName: '',
        accountNumber: '',
        ifscCode: '',
        bankName: '',
        branchName: ''
    });

    const getToken = () => localStorage.getItem("token");

    // --- FETCH DATA ---
    useEffect(() => {
        if (user?.id) {
            fetchData();
        }
    }, [user]);

    const fetchData = async () => {
        const headers = { 'Authorization': `Bearer ${getToken()}` };
        try {
            // 1. Fetch Business Profile (Company, GST, PAN, Aadhaar yahan hain)
            const busRes = await fetch(`http://localhost:2027/api/seller/profile/${user?.id}/business`, { headers });
            if (busRes.ok) setBusinessData(await busRes.json());

            // 2. Fetch Owner Basic Details (Name, Mobile, Email from Seller Table)
            const sellRes = await fetch(`http://localhost:2027/api/seller/profile/${user?.id}`, { headers });
            if (sellRes.ok) setSellerData(await sellRes.json());

            // 3. Fetch Existing Bank Details
            const bankRes = await fetch(`http://localhost:2027/api/seller/profile/${user?.id}/bank`, { headers });
            if (bankRes.ok) {
                const data = await bankRes.json();
                if (data && data.accountNumber) setBankData(data);
            }
        } catch (e) { console.error(e); }
    };

    // --- HANDLERS ---
    const handleBankChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setBankData({ ...bankData, [e.target.name]: e.target.value });
    };

    // Auto-Fetch Bank Name from IFSC
    const handleIfscBlur = async () => {
        if (bankData.ifscCode.length === 11) {
            try {
                const res = await fetch(`https://ifsc.razorpay.com/${bankData.ifscCode}`);
                const data = await res.json();
                if (data.BANK) {
                    setBankData(prev => ({ ...prev, bankName: data.BANK, branchName: data.BRANCH }));
                }
            } catch (e) { console.error("Invalid IFSC"); }
        }
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setLoading(true);
        try {
            const res = await fetch(`http://localhost:2027/api/seller/profile/${user?.id}/bank`, {
                method: 'POST',
                headers: { 
                    'Content-Type': 'application/json', 
                    'Authorization': `Bearer ${getToken()}` 
                },
                body: JSON.stringify(bankData)
            });
            if (res.ok) alert("Bank Details Saved Successfully!");
            else alert("Failed to save.");
        } catch (e) { alert("Server Error"); }
        finally { setLoading(false); }
    };

    // Formatting Addresses
    const companyAddress = `${businessData.addressLine1 || ''}, ${businessData.city || ''}, ${businessData.state || ''} - ${businessData.postalCode || ''}`;
    const ownerAddress = `${sellerData.addressLine1 || ''}, ${sellerData.city || ''}, ${sellerData.state || ''} - ${sellerData.postalCode || ''}`;

    return (
        <div className="p-6 bg-gray-50 min-h-screen max-w-6xl mx-auto">
            <h1 className="text-3xl font-black text-gray-900 mb-8 border-b-4 border-green-600 pb-2 inline-block">
                Bank & Payout Settings
            </h1>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                
                {/* --- 1. COMPANY DETAILS (Read Only) --- */}
                <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-200">
                    <div className="flex items-center mb-4 text-blue-900 border-b pb-2">
                        <Building className="mr-2" />
                        <h2 className="text-xl font-bold">Company Details</h2>
                    </div>
                    <div className="grid grid-cols-1 gap-2">
                        <ReadOnlyField label="Company Name" value={businessData.companyName} />
                        <ReadOnlyField label="GST Number" value={businessData.gstNumber} /> {/* 🔥 Added GST Here */}
                        <ReadOnlyField label="Office Contact" value={businessData.officeContact} />
                        <ReadOnlyField label="Company Email" value={businessData.companyEmail} />
                        <ReadOnlyField label="Registered Address" value={companyAddress} />
                    </div>
                </div>

                {/* --- 2. OWNER DETAILS (Read Only) --- */}
                <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-200">
                    <div className="flex items-center mb-4 text-purple-900 border-b pb-2">
                        <User className="mr-2" />
                        <h2 className="text-xl font-bold">Owner Details</h2>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                        <ReadOnlyField label="Owner Name" value={sellerData.username} />
                        <ReadOnlyField label="Mobile" value={sellerData.mobile} />
                        
                        <div className="col-span-2">
                             <ReadOnlyField label="Email" value={sellerData.email} />
                        </div>
                        
                        {/* 🔥 FETCHED FROM BUSINESS DATA BUT SHOWN HERE */}
                        <ReadOnlyField label="PAN Number" value={businessData.panNumber} />
                        <ReadOnlyField label="Aadhaar Number" value={businessData.aadhaarNumber} />
                        
                        <div className="col-span-2">
                            <ReadOnlyField label="Owner Address" value={ownerAddress} />
                        </div>
                    </div>
                </div>

            </div>

            {/* --- 3. BANK DETAILS FORM (Editable) --- */}
            <div className="mt-8 bg-white p-8 rounded-xl shadow-lg border-t-4 border-green-600">
                <div className="flex items-center mb-6 text-green-800">
                    <Landmark className="w-8 h-8 mr-3" />
                    <h2 className="text-2xl font-bold">Bank Account Details</h2>
                </div>

                <form onSubmit={handleSubmit} className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="md:col-span-2">
                        <FormInput 
                            label="Account Holder Name" 
                            name="accountHolderName" 
                            value={bankData.accountHolderName} 
                            onChange={handleBankChange} 
                            placeholder="Name as per Bank Records" 
                            required 
                        />
                    </div>
                    
                    <FormInput 
                        label="Account Number" 
                        name="accountNumber" 
                        value={bankData.accountNumber} 
                        onChange={handleBankChange} 
                        placeholder="Enter Account Number" 
                        required 
                    />

                    <div>
                        <label className="block text-sm font-bold text-gray-700 mb-1">IFSC Code *</label>
                        <input 
                            type="text"
                            name="ifscCode"
                            value={bankData.ifscCode}
                            onChange={(e) => setBankData({ ...bankData, ifscCode: e.target.value.toUpperCase() })}
                            onBlur={handleIfscBlur}
                            placeholder="e.g. SBIN0001234"
                            className="w-full border border-gray-300 rounded-lg p-2 uppercase focus:ring-2 focus:ring-blue-900"
                            maxLength={11}
                            required
                        />
                        <p className="text-xs text-gray-500 mt-1">Auto-detects Bank & Branch</p>
                    </div>

                    <FormInput 
                        label="Bank Name" 
                        name="bankName" 
                        value={bankData.bankName} 
                        onChange={handleBankChange} 
                        placeholder="Auto-filled" 
                    />

                    <FormInput 
                        label="Branch Name" 
                        name="branchName" 
                        value={bankData.branchName} 
                        onChange={handleBankChange} 
                        placeholder="Auto-filled" 
                    />

                    <div className="md:col-span-2 flex justify-end mt-4">
                        <button
                            type="submit"
                            disabled={loading}
                            className="flex items-center bg-green-600 text-white px-8 py-3 rounded-lg font-bold hover:bg-green-700 shadow-md transition disabled:opacity-70"
                        >
                            {loading ? <Loader2 className="animate-spin mr-2" /> : <Save className="mr-2" />}
                            {loading ? 'Saving Details...' : 'Save Bank Details'}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default BankDetailsPage;